import {
  displayDate,
  displayTime,
  parseDate,
} from '@adsk/alloy-react-datetime';
import { REPORT_TYPES, report } from '@adsk/exo-skeleton-api';

import i18n from '../helpers/i18n';

const datesAreEqual = (dateA: Date, dateB: Date): boolean =>
  dateA.getDate() === dateB.getDate() &&
  dateA.getMonth() === dateB.getMonth() &&
  dateA.getFullYear() === dateB.getFullYear();

export const formatDate = (input?: string | null): string => {
  if (!input) {
    return '';
  }
  const date = parseDate(input);
  const now = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (datesAreEqual(date, now)) {
    return i18n.t('TODAY_AT', {
      time: displayTime(date, { format: 'short' }),
    });
  } else if (datesAreEqual(date, yesterday)) {
    return i18n.t('YESTERDAY_AT', {
      time: displayTime(date, { format: 'short' }),
    });
  } else {
    // If a project was created before today, show the date in the format
    // Month DD, YYYY (localized)
    return displayDate(date, { format: 'short' });
  }
};

export const formatLongDate = (input?: string | null): string => {
  if (!input) {
    return '';
  }
  const date = parseDate(input);

  return `${displayDate(date, {
    format: 'short',
  })} ${displayTime(date, {
    format: 'short',
  })}`;
};

//Format date to ISOString (format supported by admin api)
export function formatDateToISOString(date: string | null | undefined): string {
  if (!date) return '';

  const formattedDate = new Date(date);
  if (formattedDate.toString() === 'Invalid Date') {
    report({
      type: REPORT_TYPES.warning,
      value: '[ProjectList] - Invalid Date was sent to formatDateToISOString',
      scope: 'accProjectList',
      context: {
        scope: 'accProjectList',
        date,
      },
    });
    return '';
  } else {
    return formattedDate.toISOString();
  }
}

//Format date from url
export const parseDateFromUrl = (dateString: string | null | undefined) => {
  if (!dateString) {
    return null;
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getDate())) {
      throw Error('Date is invalid');
    }
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
    );
  } catch {
    report({
      type: REPORT_TYPES.warning,
      value: '[ProjectList] - Date could not be parsed - parseDateFromUrl',
      scope: 'accProjectList',
      context: {
        scope: 'accProjectList',
        date: dateString,
      },
    });
    return null;
  }
};

export const formatDateToUrl = (date: Date | null | undefined): string => {
  if (!date) {
    return '';
  }
  if (date.toString() === 'Invalid Date') {
    report({
      type: REPORT_TYPES.warning,
      value: '[ProjectList] - Invalid Date was sent to formatDateToUrl',
      scope: 'accProjectList',
      context: {
        scope: 'accProjectList',
      },
    });
    return '';
  }

  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return date.getFullYear() + '-' + month + '-' + day;
};
